

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import BackButton from '../../components/common/BackButton';

const G     = '#1A6B3C';
const LIGHT = '#EAF3DE';

const STATUS_BG = {
  growing:'#DCFCE7', ready_to_sell:'#D1FAE5', sold:'#DBEAFE', closed:'#F3F4F6',
};
const STATUS_COLORS = {
  growing:'#166534', ready_to_sell:'#065F46', sold:'#1E40AF', closed:'#6B7280',
};
const STATUS_LABELS = {
  growing:' Growing', ready_to_sell:'✅ Ready to Sell', sold:'🤝 Sold', closed:'🔒 Closed',
};
const STATUS_TEXT = {
  growing:'Growing', ready_to_sell:'Ready to Sell', sold:'Sold', closed:'Closed',
};

// ── SEO Meta Tags ─────────────────────────────────────────────
function CropSEO({ crop }) {
  useEffect(() => {
    if (!crop) return;
    const farmer   = crop.userId?.name || 'Farmer';
    const location = crop.userId?.locationAddress || crop.locationAddress || 'India';
    const city     = location.split(',')[0].trim();
    const price    = crop.price ? `₹${crop.price} ${crop.priceUnit?.replace(/_/g,' ')}` : '';
    const image    = crop.stages?.find(s => s.mediaUrls?.[0])?.mediaUrls?.[0] || 'https://zevazeva.com/logo512.png';
    const url      = `https://zevazeva.com/crop/${crop._id}`;
    const title    = crop.status === 'ready_to_sell'
      ? `Buy Fresh ${crop.cropName} from ${farmer} - ${city} ${price ? '| '+price : ''} | Zeva2`
      : `${crop.cropName} Growing Update by ${farmer} - ${city} | Zeva2`;
    const description = crop.status === 'ready_to_sell'
      ? `Fresh ${crop.cropName} available in ${city}. Contact ${farmer} directly on Zeva2.`
      : `Follow ${farmer}'s ${crop.cropName} crop from ${city}. Day ${crop.currentDayCount} of ${crop.expectedDays}.`;

    document.title = title;
    const setMeta = (selector, attr, value) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        const [attrName, attrVal] = selector.replace('meta[','').replace(']','').split('=');
        el.setAttribute(attrName.trim(), attrVal.replace(/"/g,''));
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };
    setMeta('meta[name="description"]',        'content', description);
    setMeta('meta[property="og:title"]',       'content', title);
    setMeta('meta[property="og:description"]', 'content', description);
    setMeta('meta[property="og:image"]',       'content', image);
    setMeta('meta[property="og:url"]',         'content', url);
    setMeta('meta[name="twitter:card"]',       'content', 'summary_large_image');
    setMeta('meta[name="twitter:title"]',      'content', title);
    setMeta('meta[name="twitter:image"]',      'content', image);

    const existing = document.getElementById('crop-jsonld');
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.id = 'crop-jsonld';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(crop.status === 'ready_to_sell' ? {
      '@context':'https://schema.org','@type':'Product',
      name:crop.cropName, description, image:[image], url,
      offers:{'@type':'Offer', price:crop.price||0, priceCurrency:'INR',
        availability:'https://schema.org/InStock',
        seller:{'@type':'Person',name:farmer,address:{'@type':'PostalAddress',addressLocality:city,addressCountry:'IN'}}}
    } : {
      '@context':'https://schema.org','@type':'Article',
      headline:title, description, image:[image], url,
      author:{'@type':'Person',name:farmer},
      publisher:{'@type':'Organization',name:'Zeva2',url:'https://zevazeva.com'},
      datePublished:crop.createdAt, dateModified:crop.updatedAt,
    });
    document.head.appendChild(script);

    return () => {
      document.title = 'Zeva2 - Fresh. Direct. Trusted.';
      const s = document.getElementById('crop-jsonld');
      if (s) s.remove();
    };
  }, [crop]);
  return null;
}

// ── Contact Reveal ────────────────────────────────────────────
function ContactReveal({ crop }) {
  const [state, setState]         = useState('loading');
  const [mobile, setMobile]       = useState('');
  const [sub, setSub]             = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => { checkQuota(); }, []);

  const checkQuota = async () => {
    try {
      const { data } = await api.get('/subscription/me');
      const s = data.subscription;
      setSub(s);
      if (!s.isActive || s.contactsUsed >= s.contactLimit) {
        setState('hidden');
      } else {
        const res = await api.post('/subscription/reveal', { farmerId: crop.userId._id, cropId: crop._id });
        setMobile(res.data.mobile);
        setSub(p => ({ ...p, remaining: res.data.remaining, contactsUsed: res.data.contactsUsed }));
        setState('visible');
      }
    } catch (err) {
      if (err.response?.data?.code === 'QUOTA_EXCEEDED') {
        setSub(err.response.data); setState('hidden');
      } else {
        setMobile(crop.userId?.mobile || ''); setState('visible');
      }
    }
  };

  return (
    <>
      <div style={{ background:LIGHT, border:`1.5px solid ${G}`, borderRadius:16, padding:16, marginBottom:14 }}>
        <div style={{ display:'flex', alignItems:'center', gap:6, fontWeight:700, color:G, marginBottom:10 }}>
          <span style={{ width:8, height:8, borderRadius:'50%', background:G, display:'inline-block' }} /> Available to Buy
        </div>
        {crop.price && <div style={{ fontSize:16, fontWeight:800, color:'#1a1a1a', marginBottom:10 }}>₹{crop.price} {crop.priceUnit?.replace(/_/g,' ')}</div>}
        {state === 'loading' && (
          <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', background:'white', borderRadius:12, border:'1px solid #DCE9CE' }}>
            <span style={{ width:16, height:16, border:'2px solid #ddd', borderTopColor:G, borderRadius:'50%', animation:'spin .7s linear infinite', display:'inline-block' }} />
            <span style={{ fontSize:13, color:'#6B7280' }}>Loading contact...</span>
          </div>
        )}
        {state === 'visible' && (
          <>
            <div style={{ padding:'12px', background:'white', borderRadius:14, marginBottom:10, border:'1.5px solid #c8e6d0' }}>
              <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                <div style={{ width:38, height:38, borderRadius:'50%', background:LIGHT, border:`1.5px solid ${G}`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:17, flexShrink:0 }}>📞</div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:11, color:'#9CA3AF' }}>Farmer contact</div>
                  <div style={{ fontSize:16, fontWeight:800, color:'#1a1a1a', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>+91 {mobile}</div>
                </div>
              </div>
              <a href={"tel:+91"+mobile}
                style={{ marginTop:10, background:G, color:'white', border:'none', borderRadius:12, padding:'10px 16px', fontSize:12.5, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:6, textDecoration:'none', width:'100%', boxSizing:'border-box' }}>
                📞 Call Farmer
              </a>
            </div>
            {sub?.remaining !== undefined && (
              <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:6, padding:'6px 12px', background:'#f0fdf4', borderRadius:10 }}>
                <span style={{ fontSize:13 }}>✅</span>
                <span style={{ fontSize:12, color:'#166534', fontWeight:500 }}>{sub.remaining} contacts remaining · {sub.contactsUsed}/{sub.contactLimit} used</span>
              </div>
            )}
          </>
        )}
        {state === 'hidden' && (
          <>
            <div style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 14px', background:'#FEF3C7', borderRadius:12, marginBottom:10, border:'1px solid #FCD34D' }}>
              <span style={{ fontSize:20 }}>🔒</span>
              <span style={{ fontSize:17, fontWeight:700, color:'#92400E', letterSpacing:4, filter:'blur(7px)', userSelect:'none', flex:1 }}>+91 98765 43210</span>
            </div>
            <button onClick={() => setShowModal(true)}
              style={{ width:'100%', padding:13, background:'#F59E0B', color:'white', border:'none', borderRadius:12, fontSize:13.5, fontWeight:700, cursor:'pointer' }}>
              ⭐ Upgrade to See Contact
            </button>
          </>
        )}
      </div>
      {showModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.6)', zIndex:1000, display:'flex', alignItems:'flex-end' }} onClick={() => setShowModal(false)}>
          <div style={{ background:'white', borderRadius:'20px 20px 0 0', width:'100%', padding:20 }} onClick={e => e.stopPropagation()}>
            <div style={{ textAlign:'center', marginBottom:16 }}>
              <div style={{ fontSize:36, marginBottom:8 }}>🔒</div>
              <div style={{ fontSize:17, fontWeight:800 }}>Contact limit reached</div>
              <div style={{ fontSize:13, color:'#6B7280', marginTop:6 }}>Upgrade to see more farmer numbers.</div>
            </div>
            {[
              { icon:'🌅', name:'Daily',   price:'₹99',  contacts:'50 contacts',  validity:'1 day',   color:'#2563EB', popular:false },
              { icon:'📅', name:'Monthly', price:'₹499', contacts:'100 contacts', validity:'30 days', color:G,         popular:true  },
            ].map(plan => (
              <div key={plan.name} onClick={() => { setShowModal(false); toast("Contact admin on WhatsApp to activate "+plan.name+" plan for "+plan.price, { duration:4000, icon:'📱' }); }}
                style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 14px', border:'2px solid '+(plan.popular?plan.color:'#E5E7EB'), borderRadius:12, marginBottom:10, cursor:'pointer', background:plan.popular?LIGHT:'white' }}>
                <span style={{ fontSize:26 }}>{plan.icon}</span>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700 }}>{plan.name} — {plan.price}</div>
                  <div style={{ fontSize:12, color:'#6B7280' }}>{plan.contacts} · Valid {plan.validity}</div>
                </div>
                {plan.popular && <span style={{ background:G, color:'white', fontSize:10, padding:'2px 8px', borderRadius:10 }}>Best value</span>}
              </div>
            ))}
            <a href="https://wa.me/919876543210?text=Hi, I want to upgrade my Zeva2 subscription"
              target="_blank" rel="noreferrer"
              style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:8, width:'100%', padding:13, background:'#25D366', color:'white', border:'none', borderRadius:12, fontSize:14, fontWeight:700, textDecoration:'none', marginBottom:8 }}>
              📲 Contact on WhatsApp to Upgrade
            </a>
            <button onClick={() => setShowModal(false)} style={{ width:'100%', padding:10, background:'#f1f5f9', border:'none', borderRadius:10, fontSize:13, cursor:'pointer', color:'#6B7280' }}>Maybe later</button>
          </div>
        </div>
      )}
    </>
  );
}

function StatItem({ icon, label, value }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
      <div style={{ width:36, height:36, borderRadius:'50%', background:LIGHT, display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, flexShrink:0 }}>{icon}</div>
      <div style={{ minWidth:0 }}>
        <div style={{ fontSize:11, color:'#9CA3AF' }}>{label}</div>
        <div style={{ fontSize:13.5, fontWeight:800, color:'#1a1a1a' }}>{value}</div>
      </div>
    </div>
  );
}

function ActionBtn({ icon, label, count, onClick, active, activeColor }) {
  return (
    <button onClick={onClick}
      style={{ flex:1, background:'none', border:'none', cursor:'pointer', display:'flex', flexDirection:'column', alignItems:'center', gap:4, padding:'12px 4px' }}>
      <span style={{ fontSize:19, color: active ? activeColor : '#6B7280' }}>{icon}</span>
      <span style={{ fontSize:11, fontWeight:600, color: active ? activeColor : '#6B7280' }}>
        {label}{count > 0 ? ` · ${count}` : ''}
      </span>
    </button>
  );
}

// ── Main Page ─────────────────────────────────────────────────
export default function CropDetailPage() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [crop, setCrop]                   = useState(null);
  const [comments, setComments]           = useState([]);
  const [loading, setLoading]             = useState(true);
  const [showAllStages, setShowAllStages] = useState(false);
  const [showComments, setShowComments]   = useState(false);
  const [commentText, setCommentText]     = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [actionLoading, setActionLoading] = useState('');
  const [activeImg, setActiveImg]         = useState(0);

  useEffect(() => { loadCrop(); }, [id]);

  const loadCrop = async () => {
    try {
      const { data } = await api.get(`/crops/${id}?withComments=true`);
      setCrop(data.crop);
      setComments(data.comments || []);
    } catch {
      toast.error('Crop not found');
      navigate(-1);
    } finally { setLoading(false); }
  };

  const handleLike = async () => {
    if (!user) return toast.error('Login to like');
    try {
      const { data } = await api.post(`/crops/${id}/like`);
      setCrop(p => ({ ...p, likeCount: data.likeCount, isLiked: data.liked }));
    } catch { toast.error('Error'); }
  };

  const handleBest = async () => {
    if (!user) return toast.error('Login to rate');
    try {
      const { data } = await api.post(`/crops/${id}/best`);
      setCrop(p => ({ ...p, bestCropCount: data.bestCropCount, isBestCrop: data.starred }));
    } catch { toast.error('Error'); }
  };

  const handleComment = async () => {
    if (!user) return toast.error('Login to comment');
    if (!commentText.trim()) return;
    setActionLoading('comment');
    try {
      const { data } = await api.post(`/crops/${id}/comments`, { commentText });
      setComments(p => [data.comment, ...p]);
      setCrop(p => ({ ...p, commentCount: (p.commentCount || 0) + 1 }));
      setCommentText('');
    } catch { toast.error('Error posting comment'); }
    finally { setActionLoading(''); }
  };

  const handleDelete = async () => {
    setActionLoading('delete');
    try {
      await api.delete(`/crops/${id}`);
      toast.success('Crop deleted successfully');
      navigate(-1);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete crop');
    } finally { setActionLoading(''); setShowDeleteConfirm(false); }
  };

  // ── Status change — no price form needed ─────────────────
  const handleStatus = async (status) => {
    setActionLoading(status);
    try {
      const { data } = await api.put(`/crops/${id}/status`, { status });
      setCrop(p => ({ ...p, status: data.crop.status }));
      toast.success(`Status updated to ${status.replace(/_/g, ' ')}`);
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
    finally { setActionLoading(''); }
  };

  const handleWhatsAppShare = () => {
    const farmer   = crop.userId?.name || 'Farmer';
    const location = crop.userId?.locationAddress?.split(',')[0] || '';
    const price    = crop.price ? `💰 ₹${crop.price}/${crop.priceUnit?.replace('per_','') || 'kg'}` : '';
    const url      = `https://zevazeva.com/crop/${crop._id}`;
    const text     = ` *${crop.cropName}* for sale!\n ${location}\n${price}\n👨‍🌾 Farmer: ${farmer}\n\n👉 View crop: ${url}\n\n_Shared via Zeva2 - Fresh. Direct. Trusted._`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'60vh' }}>
      <div style={{ width:36, height:36, border:'3px solid #E5E7EB', borderTopColor:G, borderRadius:'50%', animation:'spin .8s linear infinite' }} />
    </div>
  );
  if (!crop) return null;

  const isOwner     = user && crop.userId?._id === user._id;
  const latestStage = crop.stages?.[crop.stages.length - 1];
  const allMedia    = latestStage?.mediaUrls?.filter(u => u && !u.startsWith('data:')) || [];
  const stages      = crop.stages || [];

  return (
    <div style={{ paddingBottom:90, background:'#F5F7FA', minHeight:'100vh', fontFamily:'Inter,system-ui,sans-serif' }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <CropSEO crop={crop} />

      {/* Header */}
      <div style={{ background:'white', padding:'14px 16px', display:'flex', alignItems:'center', gap:12, borderBottom:'0.5px solid #E5E7EB', position:'sticky', top:0, zIndex:10 }}>
        <BackButton dark />
        <div style={{ flex:1, minWidth:0 }}>
          <h1 style={{ fontSize:17, fontWeight:800, color:'#1a1a1a', margin:0 }}>{crop.cropName}</h1>
          <span style={{ fontSize:12, color:'#9CA3AF', textTransform:'capitalize' }}>{crop.category} · {crop.subCategory}</span>
        </div>
        <button onClick={handleLike} style={{ background:'none', border:'none', fontSize:19, cursor:'pointer', color: crop.isLiked ? '#EF4444' : '#1a1a1a', flexShrink:0 }}>
          {crop.isLiked ? '♥' : '♡'}
        </button>
      </div>

      <div style={{ padding:'14px 14px 0', maxWidth:480, margin:'0 auto' }}>

        {/* Media gallery */}
        <div style={{ position:'relative', background:LIGHT, height:230, borderRadius:18, overflow:'hidden', marginBottom:16 }}>
          {allMedia.length > 0 ? (
            <img src={allMedia[activeImg]} alt={crop.cropName} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
          ) : (
            <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:56 }}></div>
          )}
          <div style={{ position:'absolute', top:12, left:12, background:'white', borderRadius:20, padding:'6px 12px', display:'flex', alignItems:'center', gap:6, boxShadow:'0 2px 8px rgba(0,0,0,.1)' }}>
            <span style={{ width:8, height:8, borderRadius:'50%', background:STATUS_COLORS[crop.status], display:'inline-block' }} />
            <span style={{ fontSize:12, fontWeight:700, color:STATUS_COLORS[crop.status] }}>{STATUS_TEXT[crop.status]}</span>
          </div>
          {allMedia.length > 1 && (
            <div style={{ position:'absolute', bottom:12, left:0, right:0, display:'flex', justifyContent:'center', gap:5 }}>
              {allMedia.map((_, i) => (
                <button key={i} onClick={() => setActiveImg(i)}
                  style={{ width:i===activeImg?16:6, height:6, borderRadius:3, background:i===activeImg?'white':'rgba(255,255,255,0.5)', border:'none', transition:'width 0.2s', padding:0 }} />
              ))}
            </div>
          )}
          <div style={{ position:'absolute', bottom:12, right:12, background:'rgba(0,0,0,0.6)', color:'white', borderRadius:20, padding:'5px 12px', fontSize:12.5, fontWeight:700 }}>
            Day {crop.currentDayCount}
          </div>
        </div>

        {/* Farmer info */}
        {crop.userId && (
          <div style={{ background:'white', borderRadius:16, border:'0.5px solid #E5E7EB', padding:'14px', marginBottom:14, display:'flex', alignItems:'center', gap:12 }}>
            <div style={{ width:48, height:48, borderRadius:'50%', background:LIGHT, display:'flex', alignItems:'center', justifyContent:'center', color:G, fontWeight:800, fontSize:19, flexShrink:0 }}>
              {crop.userId.name?.[0] || 'F'}
            </div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <span style={{ fontWeight:800, fontSize:15, color:'#1a1a1a' }}>{crop.userId.name || 'Farmer'}</span>
                {crop.userId.rating && (
                  <span style={{ background:'#FEF3C7', color:'#92400E', fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:10, display:'flex', alignItems:'center', gap:3 }}>
                    ★ {crop.userId.rating}
                  </span>
                )}
              </div>
              <div style={{ fontSize:12, color:'#9CA3AF', marginTop:2 }}> {crop.userId.locationAddress || crop.locationAddress || 'Location not set'}</div>
              {crop.userId.isVerified && (
                <div style={{ fontSize:11.5, color:G, fontWeight:700, marginTop:3, display:'flex', alignItems:'center', gap:4 }}>
                  <span style={{ width:14, height:14, borderRadius:'50%', background:G, color:'white', fontSize:9, display:'flex', alignItems:'center', justifyContent:'center' }}>✓</span>
                  Verified Farmer
                </div>
              )}
            </div>
            {!isOwner && (
              <button onClick={() => navigate(`/farmer/${crop.userId._id}`)}
                style={{ background:'white', border:`1.5px solid ${G}`, color:G, borderRadius:12, padding:'8px 14px', fontSize:12, fontWeight:700, cursor:'pointer', flexShrink:0 }}>
                View Profile
              </button>
            )}
          </div>
        )}

        {/* Stats grid */}
        <div style={{ background:'white', borderRadius:16, border:'0.5px solid #E5E7EB', padding:16, marginBottom:14, display:'grid', gridTemplateColumns:'repeat(2, minmax(0,1fr))', rowGap:16, columnGap:10 }}>
          <StatItem icon="" label="Start Date" value={new Date(crop.startDate).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' })} />
          <StatItem icon="📅" label="Today" value={`Day ${crop.currentDayCount}`} />
          <StatItem icon="⏱️" label="Expected Duration" value={`${crop.expectedDays} Days`} />
          <StatItem icon="✅" label="Status" value={STATUS_TEXT[crop.status]} />
        </div>

        {latestStage?.description && (
          <div style={{ background:'white', borderRadius:16, border:'0.5px solid #E5E7EB', padding:14, marginBottom:14 }}>
            <div style={{ fontWeight:700, fontSize:13, color:'#1a1a1a', marginBottom:6 }}>{latestStage.stageName || 'Latest update'}</div>
            <p style={{ fontSize:13.5, color:'#4B5563', lineHeight:1.5, margin:0 }}>{latestStage.description}</p>
          </div>
        )}

        {/* Social actions */}
        <div style={{ background:'white', borderRadius:16, border:'0.5px solid #E5E7EB', marginBottom:14, display:'flex' }}>
          <ActionBtn icon={crop.isLiked ? '♥' : '♡'} label="Like" count={crop.likeCount} onClick={handleLike} active={crop.isLiked} activeColor="#EF4444" />
          <div style={{ width:'0.5px', background:'#E5E7EB' }} />
          <ActionBtn icon={crop.isBestCrop ? '★' : '☆'} label="Favorite" count={crop.bestCropCount} onClick={handleBest} active={crop.isBestCrop} activeColor="#D97706" />
          <div style={{ width:'0.5px', background:'#E5E7EB' }} />
          <ActionBtn icon="💬" label="Chat" count={crop.commentCount} onClick={() => setShowComments(p => !p)} active={showComments} activeColor={G} />
          <div style={{ width:'0.5px', background:'#E5E7EB' }} />
          <ActionBtn icon="🔗" label="Share" onClick={handleWhatsAppShare} activeColor="#25D366" />
        </div>

        {/* Comments */}
        {showComments && (
          <div style={{ background:'white', borderRadius:16, border:'0.5px solid #E5E7EB', padding:14, marginBottom:14 }}>
            <div style={{ display:'flex', gap:8, marginBottom:12 }}>
              <input placeholder="Write a comment..." value={commentText}
                onChange={e => setCommentText(e.target.value)} onKeyDown={e => e.key==='Enter' && handleComment()}
                style={{ flex:1, border:'1px solid #E5E7EB', borderRadius:12, padding:'9px 12px', fontSize:13, outline:'none' }} />
              <button onClick={handleComment} disabled={actionLoading==='comment'}
                style={{ background:G, color:'white', border:'none', borderRadius:12, padding:'9px 16px', fontSize:12.5, fontWeight:700, cursor:'pointer' }}>
                Post
              </button>
            </div>
            {comments.map(c => (
              <div key={c._id} style={{ display:'flex', gap:8, marginBottom:10 }}>
                <div style={{ width:30, height:30, borderRadius:'50%', background:G, color:'white', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:700, flexShrink:0 }}>
                  {c.userId?.name?.[0] || 'U'}
                </div>
                <div style={{ flex:1, background:'#F5F7FA', borderRadius:12, padding:'8px 10px' }}>
                  <div style={{ fontWeight:700, fontSize:12.5, color:'#1a1a1a' }}>{c.userId?.name || 'User'}</div>
                  <div style={{ fontSize:13, color:'#4B5563', marginTop:2 }}>{c.commentText}</div>
                </div>
              </div>
            ))}
            {comments.length === 0 && <p style={{ color:'#9CA3AF', fontSize:13, textAlign:'center' }}>No comments yet. Be first!</p>}
          </div>
        )}

        {/* Contact Farmer */}
        {crop.userId?.role === 'seller' && !isOwner && crop.status === 'ready_to_sell' && (user?.role === 'buyer' || user?.role === 'agent') && (
          <ContactReveal crop={crop} />
        )}

        {!user && crop.status === 'ready_to_sell' && (
          <div style={{ background:'#E6F0FF', border:'1px solid #93C5FD', borderRadius:16, padding:14, marginBottom:14, textAlign:'center' }}>
            <div style={{ fontSize:14, fontWeight:700, color:'#1D4ED8', marginBottom:8 }}>🔑 Login to contact this farmer</div>
            <button onClick={() => navigate('/login')}
              style={{ background:'#1D4ED8', color:'white', border:'none', borderRadius:12, padding:'10px 20px', fontSize:13, fontWeight:700, cursor:'pointer' }}>
              Login / Register
            </button>
          </div>
        )}

        {user?.role === 'seller' && !isOwner && crop.status === 'ready_to_sell' && (
          <div style={{ background:'white', border:'0.5px solid #E5E7EB', borderRadius:14, padding:12, marginBottom:14, textAlign:'center' }}>
            <div style={{ fontSize:13, color:'#6B7280' }}> Listed for sale</div>
          </div>
        )}

        {(user?.role === 'buyer' || user?.role === 'agent') && !isOwner && (
          <div style={{ display:'flex', gap:10, marginBottom:14 }}>
            <button onClick={() => navigate('/requests/new', { state:{ cropId:crop._id, cropName:crop.cropName, category:crop.category, subCategory:crop.subCategory } })}
              style={{ flex:1, background:G, color:'white', border:'none', borderRadius:14, padding:13, fontSize:13, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
              📦 Request This Crop
            </button>
            <button onClick={() => setShowComments(true)}
              style={{ flex:1, background:'white', color:G, border:`1.5px solid ${G}`, borderRadius:14, padding:13, fontSize:13, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
              💬 Send Inquiry
            </button>
          </div>
        )}

        {/* ── Manage Crop (owner only) ── */}
        {isOwner && (
          <div style={{ background:'white', borderRadius:16, border:'0.5px solid #E5E7EB', padding:14, marginBottom:14 }}>
            <div style={{ fontWeight:700, fontSize:13.5, color:'#1a1a1a', marginBottom:12 }}>Manage Crop</div>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              {crop.status === 'growing' && (
                <button onClick={() => handleStatus('ready_to_sell')} disabled={actionLoading==='ready_to_sell'}
                  style={{ background:G, color:'white', border:'none', borderRadius:12, padding:'9px 16px', fontSize:12.5, fontWeight:700, cursor:'pointer' }}>
                  {actionLoading==='ready_to_sell' ? '…' : '🟢 Mark Ready to Sell'}
                </button>
              )}
              {crop.status === 'ready_to_sell' && (
                <button onClick={() => handleStatus('sold')} disabled={actionLoading==='sold'}
                  style={{ background:'white', color:'#1D4ED8', border:'1.5px solid #1D4ED8', borderRadius:12, padding:'9px 16px', fontSize:12.5, fontWeight:700, cursor:'pointer' }}>
                  {actionLoading==='sold' ? '…' : '✅ Mark as Sold'}
                </button>
              )}
              {['growing','ready_to_sell'].includes(crop.status) && (
                <button onClick={() => handleStatus('closed')} disabled={actionLoading==='closed'}
                  style={{ background:'#F1F3EE', color:'#4B5563', border:'none', borderRadius:12, padding:'9px 16px', fontSize:12.5, fontWeight:700, cursor:'pointer' }}>
                  {actionLoading==='closed' ? '…' : '🔒 Close'}
                </button>
              )}
              <button onClick={() => navigate(`/crop/${id}/add-stage`)}
                style={{ background:G, color:'white', border:'none', borderRadius:12, padding:'9px 16px', fontSize:12.5, fontWeight:700, cursor:'pointer' }}>
                + Add Stage
              </button>
              <button onClick={() => setShowDeleteConfirm(true)} disabled={actionLoading==='delete'}
                style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:6, padding:'9px 14px', background:'#FEF2F2', color:'#DC2626', border:'1.5px solid #FECACA', borderRadius:12, fontSize:12.5, fontWeight:700, cursor:'pointer' }}>
                🗑️ Delete
              </button>
            </div>
          </div>
        )}

        {/* Delete confirm */}
        {showDeleteConfirm && (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.55)', zIndex:1000, display:'flex', alignItems:'flex-end' }} onClick={() => setShowDeleteConfirm(false)}>
            <div style={{ background:'white', borderRadius:'20px 20px 0 0', width:'100%', padding:24 }} onClick={e => e.stopPropagation()}>
              <div style={{ textAlign:'center', marginBottom:20 }}>
                <div style={{ fontSize:42, marginBottom:10 }}>🗑️</div>
                <div style={{ fontSize:17, fontWeight:800, marginBottom:6 }}>Delete "{crop.cropName}"?</div>
                <div style={{ fontSize:13, color:'#6B7280' }}>This cannot be undone. <strong style={{ color:'#DC2626' }}>All stages deleted.</strong></div>
              </div>
              <div style={{ display:'flex', gap:10 }}>
                <button onClick={() => setShowDeleteConfirm(false)}
                  style={{ flex:1, padding:13, background:'#F1F5F9', border:'none', borderRadius:12, fontSize:14, fontWeight:700, cursor:'pointer', color:'#6B7280' }}>
                  Cancel
                </button>
                <button onClick={handleDelete} disabled={actionLoading==='delete'}
                  style={{ flex:1, padding:13, background:'#DC2626', color:'white', border:'none', borderRadius:12, fontSize:14, fontWeight:700, cursor:'pointer' }}>
                  {actionLoading==='delete' ? '…' : '🗑️ Yes, Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Stage timeline */}
        <div style={{ background:'white', borderRadius:16, border:'0.5px solid #E5E7EB', padding:14, marginBottom:20 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: stages.length ? 14 : 0 }}>
            <span style={{ fontWeight:800, fontSize:14.5, color:'#1a1a1a' }}>Crop Stages ({stages.length})</span>
            {stages.length > 0 && (
              <button onClick={() => setShowAllStages(p => !p)}
                style={{ background:'none', border:'none', color:G, fontSize:12.5, fontWeight:700, cursor:'pointer' }}>
                {showAllStages ? 'Hide' : 'View All'} ›
              </button>
            )}
          </div>

          {stages.length > 0 && (
            <div style={{ display:'flex', alignItems:'flex-start', overflowX:'auto', paddingBottom:4 }}>
              {stages.map((stage, i) => {
                const thumb = stage.mediaUrls?.find(u => u && !u.startsWith('data:'));
                return (
                  <React.Fragment key={i}>
                    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', width:96, flexShrink:0, textAlign:'center' }}>
                      <div style={{ position:'relative', width:56, height:56, borderRadius:'50%', overflow:'hidden', background:LIGHT, border:`2px solid ${G}` }}>
                        {thumb
                          ? <img src={thumb} alt={stage.stageName} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                          : <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:22 }}></div>}
                        <span style={{ position:'absolute', bottom:-2, right:-2, width:18, height:18, borderRadius:'50%', background:G, color:'white', fontSize:10, display:'flex', alignItems:'center', justifyContent:'center', border:'2px solid white' }}>✓</span>
                      </div>
                      <div style={{ fontSize:11.5, fontWeight:700, color:'#1a1a1a', marginTop:8 }}>Stage {i+1}</div>
                      <div style={{ fontSize:10.5, color:'#4B5563' }}>{stage.stageName}</div>
                      <div style={{ fontSize:9.5, color:'#9CA3AF', marginTop:1 }}>
                        {stage.uploadedAt ? new Date(stage.uploadedAt).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' }) : ''}
                      </div>
                    </div>
                    {i < stages.length - 1 && (
                      <div style={{ flex:1, minWidth:20, height:2, background:'transparent', borderTop:`2px dashed ${G}55`, marginTop:28 }} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          )}

          {showAllStages && [...stages].reverse().map((stage, i) => (
            <div key={i} style={{ background:'#F5F7FA', borderRadius:14, padding:12, marginTop:14, borderLeft:`4px solid ${G}` }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
                <span style={{ fontWeight:700, fontSize:13.5, color:'#1a1a1a' }}>{stage.stageName}</span>
                <span style={{ background:LIGHT, color:G, borderRadius:10, padding:'2px 8px', fontSize:11.5, fontWeight:700 }}>Day {stage.dayCount}</span>
              </div>
              {stage.mediaUrls?.filter(u => !u.startsWith('data:')).length > 0 && (
                <div style={{ display:'flex', gap:6, marginBottom:6, overflow:'auto' }}>
                  {stage.mediaUrls.filter(u => !u.startsWith('data:')).map((url, j) => (
                    <img key={j} src={url} alt={`${crop.cropName} stage ${stage.stageNumber}`} style={{ width:70, height:70, objectFit:'cover', borderRadius:10, flexShrink:0 }} />
                  ))}
                </div>
              )}
              {stage.description && <p style={{ fontSize:13, color:'#4B5563', margin:0 }}>{stage.description}</p>}
              <div style={{ fontSize:11, color:'#9CA3AF', marginTop:4 }}>
                {stage.uploadedAt ? new Date(stage.uploadedAt).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' }) : ''}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}